import {
  billingInvoiceCreateRequestSchema,
  billingInvoiceMutationResponseSchema,
  billingInvoiceStatusUpdateRequestSchema,
  billingPlanMutationResponseSchema,
  billingPlanUpdateRequestSchema,
  dutyRosterResponseSchema,
  dutyRosterUpdateRequestSchema,
  hqAuditResponseSchema,
  hqActiveImpersonationResponseSchema,
  hqImpersonationEndResponseSchema,
  hqImpersonationMutationResponseSchema,
  hqImpersonationStartRequestSchema,
  hqMfaPolicyResponseSchema,
  hqMfaPolicyUpdateRequestSchema,
  hqSystemStatusResponseSchema,
  hqTeamMemberMutationResponseSchema,
  hqTeamResponseSchema,
  hqTeamRoleUpdateRequestSchema,
  hqDashboardResponseSchema,
  hqSearchResponseSchema,
  hqBillingResponseSchema,
  hqPilotCreateRequestSchema,
  hqPilotDailyReviewCreateRequestSchema,
  hqPilotDailyReviewMutationResponseSchema,
  hqPilotDailyReviewsResponseSchema,
  hqPilotFeedbackCreateRequestSchema,
  hqPilotFeedbackMutationResponseSchema,
  hqPilotFeedbackResponseSchema,
  hqPilotOperationalDrillCreateRequestSchema,
  hqPilotOperationalDrillMutationResponseSchema,
  hqPilotOperationalDrillsResponseSchema,
  hqPilotCloseoutCreateRequestSchema,
  hqPilotCloseoutMutationResponseSchema,
  hqPilotCloseoutResponseSchema,
  hqPilotMutationResponseSchema,
  hqPilotsResponseSchema,
  hqPilotUpdateRequestSchema,
  hqSchoolCreateRequestSchema,
  hqSchoolCreateResponseSchema,
  hqSchoolDetailsResponseSchema,
  hqSchoolUpdateRequestSchema,
  hqSubjectCatalogResponseSchema,
  hqSubjectCatalogUpdateRequestSchema,
  hqSchoolsResponseSchema,
  notificationMutationResponseSchema,
  hqNotificationsResponseSchema,
  platformAiConnectionTestResponseSchema,
  platformAiSettingsResponseSchema,
  platformAiSettingsSchema,
  openAiModelsResponseSchema,
  schoolAiSettingsUpdateRequestSchema,
  schoolAiSettingsResponseSchema,
  hqConversationArchiveResponseSchema,
  platformCostSettingsResponseSchema,
  platformCostSettingsSchema,
  platformSupportPolicyResponseSchema,
  supportPolicySchema,
  schoolLimitOverridesSchema,
  schoolLimitsResponseSchema,
  schoolInvitationCreateRequestSchema,
  schoolInvitationCreateResponseSchema,
  schoolReadinessResponseSchema,
  type PlatformCostSettings,
  type BillingPlanUpdateRequest,
  type BillingInvoiceCreateRequest,
  type BillingInvoiceStatusUpdateRequest,
  type DataExportFormat,
  type DutyRosterUpdateRequest,
  type HqSchoolCreateRequest,
  type HqSchoolsQuery,
  type HqRole,
  type HqImpersonationStartRequest,
  type HqMfaPolicyUpdateRequest,
  type HqSchoolUpdateRequest,
  type HqSubjectCatalogUpdateRequest,
  type HqDataExportType,
  type HqPilotCreateRequest,
  type HqPilotDailyReviewCreateRequest,
  type HqPilotFeedbackCreateRequest,
  type HqPilotOperationalDrillCreateRequest,
  type HqPilotCloseoutCreateRequest,
  type HqPilotUpdateRequest,
  type PlatformAiSettings,
  type SchoolAiSettingsUpdateRequest,
  type SupportPolicy,
  type SchoolLimitOverrides,
  type SchoolInvitationCreateRequest,
} from "@kindtutor/api-contract";
import {
  fetchApiBlob,
  fetchApiJson,
  localizedApiErrorSchema as apiErrorSchema,
  parseApiInput,
  throwApiError,
} from "@kindtutor/auth-client";

import { apiBaseURL, apiFetch } from "./auth-client";

export async function fetchActiveHqImpersonation() {
  return fetchApiJson(
    apiFetch,
    `${apiBaseURL}/v1/hq/impersonations/active`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
    hqActiveImpersonationResponseSchema,
    "Nie udało się sprawdzić dostępu do szkoły.",
  );
}

export async function startHqImpersonation(
  values: HqImpersonationStartRequest,
) {
  const input = parseApiInput(hqImpersonationStartRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/impersonations`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się uruchomić dostępu do szkoły.");
  return hqImpersonationMutationResponseSchema.parse(await response.json());
}

export async function endHqImpersonation() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/impersonations/active`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się zakończyć dostępu do szkoły.");
  return hqImpersonationEndResponseSchema.parse(await response.json());
}

export async function fetchHqTeam() {
  return fetchApiJson(
    apiFetch,
    `${apiBaseURL}/v1/hq/team`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
    hqTeamResponseSchema,
    "Nie udało się wczytać zespołu HQ.",
  );
}

export async function updateHqTeamRole(userId: string, role: HqRole) {
  const input = parseApiInput(hqTeamRoleUpdateRequestSchema, { role });
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/team/${encodeURIComponent(userId)}/role`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zmienić roli w zespole HQ.");
  return hqTeamMemberMutationResponseSchema.parse(await response.json());
}

export async function fetchHqAudit(input: {
  days: 7 | 30 | 90;
  action?: string;
  schoolId?: string;
  cursor?: string;
  limit?: number;
}) {
  const query = new URLSearchParams({ days: String(input.days) });
  if (input.action) query.set("action", input.action);
  if (input.schoolId) query.set("schoolId", input.schoolId);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const response = await apiFetch(`${apiBaseURL}/v1/hq/audit?${query}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać dziennika audytu.");
  return hqAuditResponseSchema.parse(await response.json());
}

export async function fetchHqSystemStatus() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/system-status`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać stanu platformy.");
  return hqSystemStatusResponseSchema.parse(await response.json());
}

export async function fetchHqSubjectCatalog() {
  return fetchApiJson(
    apiFetch,
    `${apiBaseURL}/v1/hq/curriculum/subjects`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
    hqSubjectCatalogResponseSchema,
    "Nie udało się wczytać globalnych przedmiotów.",
  );
}

export async function updateHqSubjectCatalog(
  values: HqSubjectCatalogUpdateRequest,
) {
  const input = parseApiInput(hqSubjectCatalogUpdateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/curriculum/subjects`, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(
      response,
      "Nie udało się zapisać globalnych przedmiotów.",
    );
  return hqSubjectCatalogResponseSchema.parse(await response.json());
}

export async function downloadHqUsageExport(input: {
  format: DataExportFormat;
  days: 30 | 90 | 365;
}) {
  const query = new URLSearchParams({
    format: input.format,
    days: String(input.days),
  });
  return fetchApiBlob(
    apiFetch,
    `${apiBaseURL}/v1/hq/exports/usage?${query}`,
    {
      credentials: "include",
      headers: { Accept: "*/*" },
    },
    "Nie udało się przygotować eksportu.",
    `kindtutor-hq-usage.${input.format}`,
  );
}

export async function downloadHqExport(input: {
  type: HqDataExportType;
  format: DataExportFormat;
  days: 30 | 90 | 365;
}) {
  const query = new URLSearchParams({
    format: input.format,
    days: String(input.days),
  });
  return fetchApiBlob(
    apiFetch,
    `${apiBaseURL}/v1/hq/exports/${input.type}?${query}`,
    { credentials: "include", headers: { Accept: "*/*" } },
    "Nie udało się przygotować eksportu.",
    `kindtutor-hq-${input.type}.${input.format}`,
  );
}

export async function fetchHqDashboard() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/dashboard`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać danych przeglądu.");
  return hqDashboardResponseSchema.parse(await response.json());
}

export async function searchHq(query: string) {
  const parameters = new URLSearchParams({ q: query });
  const response = await apiFetch(`${apiBaseURL}/v1/hq/search?${parameters}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wyszukać danych.");
  return hqSearchResponseSchema.parse(await response.json());
}

export async function fetchHqNotifications() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/notifications`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać powiadomień.");
  return hqNotificationsResponseSchema.parse(await response.json());
}

export async function markHqNotificationRead(notificationId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/notifications/${encodeURIComponent(notificationId)}/read`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się oznaczyć powiadomienia.");
  return notificationMutationResponseSchema.parse(await response.json());
}

export async function markAllHqNotificationsRead() {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/notifications/read-all`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się oznaczyć powiadomień.");
  return notificationMutationResponseSchema.parse(await response.json());
}

export async function fetchHqBilling() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/billing`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać rozliczeń.");
  return hqBillingResponseSchema.parse(await response.json());
}

export async function updateBillingPlan(
  planCode: string,
  values: BillingPlanUpdateRequest,
) {
  const input = parseApiInput(billingPlanUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/billing/plans/${encodeURIComponent(planCode)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success ? error.data.error.message : "Nie udało się zapisać planu.",
    );
  }
  return billingPlanMutationResponseSchema.parse(body);
}

export async function createBillingInvoice(
  values: BillingInvoiceCreateRequest,
) {
  const input = parseApiInput(billingInvoiceCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/billing/invoices`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się wystawić faktury.",
    );
  }
  return billingInvoiceMutationResponseSchema.parse(body);
}

export async function updateBillingInvoiceStatus(
  invoiceId: string,
  values: BillingInvoiceStatusUpdateRequest,
) {
  const input = parseApiInput(billingInvoiceStatusUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/billing/invoices/${encodeURIComponent(invoiceId)}/status`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zmienić statusu faktury.",
    );
  }
  return billingInvoiceMutationResponseSchema.parse(body);
}

export async function fetchHqPilots() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/pilots`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać pilotaży.");
  return hqPilotsResponseSchema.parse(await response.json());
}

export async function createHqPilot(values: HqPilotCreateRequest) {
  const input = parseApiInput(hqPilotCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/pilots`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć pilotażu.",
    );
  }
  return hqPilotMutationResponseSchema.parse(body);
}

export async function updateHqPilot(
  pilotId: string,
  values: HqPilotUpdateRequest,
) {
  const input = parseApiInput(hqPilotUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać pilotażu.",
    );
  }
  return hqPilotMutationResponseSchema.parse(body);
}

export async function fetchHqPilotDailyReviews(pilotId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/daily-reviews`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    await throwApiError(response, "Nie udało się wczytać przeglądów pilotażu.");
  }
  return hqPilotDailyReviewsResponseSchema.parse(await response.json());
}

export async function createHqPilotDailyReview(
  pilotId: string,
  values: HqPilotDailyReviewCreateRequest,
) {
  const input = parseApiInput(hqPilotDailyReviewCreateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/daily-reviews`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać przeglądu pilotażu.",
    );
  }
  return hqPilotDailyReviewMutationResponseSchema.parse(body);
}

export async function fetchHqPilotFeedback(pilotId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/feedback`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    await throwApiError(response, "Nie udało się wczytać feedbacku pilotażu.");
  }
  return hqPilotFeedbackResponseSchema.parse(await response.json());
}

export async function createHqPilotFeedback(
  pilotId: string,
  values: HqPilotFeedbackCreateRequest,
) {
  const input = parseApiInput(hqPilotFeedbackCreateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/feedback`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać feedbacku pilotażu.",
    );
  }
  return hqPilotFeedbackMutationResponseSchema.parse(body);
}

export async function fetchHqPilotOperationalDrills(pilotId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/operational-drills`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    await throwApiError(
      response,
      "Nie udało się wczytać prób operacyjnych pilotażu.",
    );
  }
  return hqPilotOperationalDrillsResponseSchema.parse(await response.json());
}

export async function createHqPilotOperationalDrill(
  pilotId: string,
  values: HqPilotOperationalDrillCreateRequest,
) {
  const input = parseApiInput(
    hqPilotOperationalDrillCreateRequestSchema,
    values,
  );
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/operational-drills`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać próby operacyjnej pilotażu.",
    );
  }
  return hqPilotOperationalDrillMutationResponseSchema.parse(body);
}

export async function fetchHqPilotCloseout(pilotId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/closeout`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    await throwApiError(
      response,
      "Nie udało się wczytać decyzji końcowej pilotażu.",
    );
  }
  return hqPilotCloseoutResponseSchema.parse(await response.json());
}

export async function createHqPilotCloseout(
  pilotId: string,
  values: HqPilotCloseoutCreateRequest,
) {
  const input = parseApiInput(hqPilotCloseoutCreateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/pilots/${encodeURIComponent(pilotId)}/closeout`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać decyzji końcowej pilotażu.",
    );
  }
  return hqPilotCloseoutMutationResponseSchema.parse(body);
}

export {
  convertHqSalesLead,
  createHqSalesDocument,
  createHqSalesInteraction,
  createHqSalesLead,
  createHqSalesTask,
  fetchAllHqSalesLeads,
  fetchHqSalesLead,
  fetchHqSalesLeads,
  updateHqSalesDocument,
  updateHqSalesLead,
  updateHqSalesTask,
} from "./api/sales";

export {
  fetchHqFleet,
  createHqDevice,
  fetchStationReleases,
  fetchDevicePlatformPolicies,
  updateDevicePlatformPolicy,
  createStationRelease,
  transitionStationRelease,
  fetchHqDevice,
  updateHqDevice,
  reEnrollHqDevice,
  fetchHqDeviceOperations,
  addHqDeviceAccessory,
  updateHqDeviceAccessory,
  addHqDeviceConditionCheck,
  createHqDeviceServiceOrder,
  updateHqDeviceServiceOrder,
  uploadHqDeviceOperationAttachment,
  downloadHqDeviceOperationAttachment,
  deleteHqDeviceOperationAttachment,
} from "./api/devices";

export {
  addHqIncidentComment,
  createHqIncident,
  deleteHqIncidentAttachment,
  downloadHqIncidentAttachment,
  fetchHqIncident,
  fetchHqIncidents,
  uploadHqIncidentAttachment,
  updateHqIncident,
} from "./api/incidents";

export async function fetchSupportPolicy() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/support-policy`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać zasad wsparcia.");
  return platformSupportPolicyResponseSchema.parse(await response.json());
}

export async function updateSupportPolicy(values: SupportPolicy) {
  const input = parseApiInput(supportPolicySchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/support-policy`, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać zasad wsparcia.");
  return platformSupportPolicyResponseSchema.parse(await response.json());
}

export async function fetchDutyRoster() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/duty-roster`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać obsady dyżurów.");
  return dutyRosterResponseSchema.parse(await response.json());
}

export async function updateDutyRoster(values: DutyRosterUpdateRequest) {
  const input = parseApiInput(dutyRosterUpdateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/duty-roster`, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać obsady dyżurów.");
  return dutyRosterResponseSchema.parse(await response.json());
}

export async function fetchHqMfaPolicy() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/mfa-policy`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać polityki MFA.");
  return hqMfaPolicyResponseSchema.parse(await response.json());
}

export async function updateHqMfaPolicy(values: HqMfaPolicyUpdateRequest) {
  const input = parseApiInput(hqMfaPolicyUpdateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/mfa-policy`, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać polityki MFA.");
  return hqMfaPolicyResponseSchema.parse(await response.json());
}

export async function fetchPlatformAiSettings() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/ai-settings`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać konfiguracji AI.");
  return platformAiSettingsResponseSchema.parse(await response.json());
}

export async function fetchOpenAiModels() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/ai-models`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się pobrać modeli OpenAI.");
  return openAiModelsResponseSchema.parse(await response.json());
}

export async function updatePlatformAiSettings(values: PlatformAiSettings) {
  const input = parseApiInput(platformAiSettingsSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/ai-settings`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać konfiguracji AI.",
    );
  }
  return platformAiSettingsResponseSchema.parse(body);
}

export async function testPlatformAiConnection() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/ai-settings/test`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wykonać testu połączenia.");
  return platformAiConnectionTestResponseSchema.parse(await response.json());
}

export async function rollbackPlatformAiSettings() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/ai-settings/rollback`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się przywrócić konfiguracji.",
    );
  }
  return platformAiSettingsResponseSchema.parse(body);
}

export async function fetchPlatformCostSettings() {
  const response = await apiFetch(`${apiBaseURL}/v1/hq/cost-settings`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać stawek kosztów.");
  return platformCostSettingsResponseSchema.parse(await response.json());
}

export async function updatePlatformCostSettings(values: PlatformCostSettings) {
  const input = parseApiInput(platformCostSettingsSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/cost-settings`, {
    method: "PUT",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać stawek kosztów.");
  return platformCostSettingsResponseSchema.parse(await response.json());
}

export async function rollbackPlatformCostSettings() {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/cost-settings/rollback`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się przywrócić stawek kosztów.");
  return platformCostSettingsResponseSchema.parse(await response.json());
}

export async function fetchHqSchools(input: Partial<HqSchoolsQuery> = {}) {
  const query = new URLSearchParams();
  if (input.q) query.set("q", input.q);
  if (input.status) query.set("status", input.status);
  if (input.planCode) query.set("planCode", input.planCode);
  if (input.sort) query.set("sort", input.sort);
  if (input.direction) query.set("direction", input.direction);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await apiFetch(`${apiBaseURL}/v1/hq/schools${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać szkół.");
  return hqSchoolsResponseSchema.parse(await response.json());
}

export async function fetchAllHqSchools() {
  const schools = [];
  let cursor: string | undefined;
  let lastPage: Awaited<ReturnType<typeof fetchHqSchools>> | undefined;
  for (let pageNumber = 0; pageNumber < 100; pageNumber += 1) {
    lastPage = await fetchHqSchools({ cursor, limit: 100 });
    schools.push(...lastPage.schools);
    if (!lastPage.page.hasMore || !lastPage.page.nextCursor) {
      return {
        ...lastPage,
        schools,
        page: { ...lastPage.page, hasMore: false, nextCursor: null },
      };
    }
    cursor = lastPage.page.nextCursor;
  }
  throw new Error("Lista szkół ma zbyt wiele stron do wczytania.");
}

export async function createHqSchool(values: HqSchoolCreateRequest) {
  const input = parseApiInput(hqSchoolCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/schools`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć szkoły.",
    );
  }
  return hqSchoolCreateResponseSchema.parse(body);
}

export async function fetchHqSchool(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać szkoły.");
  return hqSchoolDetailsResponseSchema.parse(await response.json());
}

export async function fetchHqSchoolAiSettings(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/ai-settings`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać ustawień AI szkoły.");
  return schoolAiSettingsResponseSchema.parse(await response.json());
}

export async function updateHqSchoolAiSettings(
  schoolId: string,
  values: SchoolAiSettingsUpdateRequest,
) {
  const input = parseApiInput(schoolAiSettingsUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/ai-settings`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać ustawień AI szkoły.");
  return schoolAiSettingsResponseSchema.parse(await response.json());
}

export async function fetchHqConversationArchive(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/conversations`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać archiwum rozmów.");
  return hqConversationArchiveResponseSchema.parse(await response.json());
}

export async function fetchHqConversationPhoto(
  schoolId: string,
  photoId: string,
) {
  return fetchApiBlob(
    apiFetch,
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/conversation-photos/${encodeURIComponent(photoId)}`,
    { credentials: "include", headers: { Accept: "image/*" } },
    "Nie udało się wczytać zdjęcia.",
  );
}

export async function fetchHqSchoolReadiness(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/readiness`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać gotowości szkoły.");
  return schoolReadinessResponseSchema.parse(await response.json());
}

export async function updateHqSchool(
  schoolId: string,
  values: HqSchoolUpdateRequest,
) {
  const input = parseApiInput(hqSchoolUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać szkoły.",
    );
  }
  return hqSchoolDetailsResponseSchema.parse(body);
}

export async function createHqSchoolAdministratorInvitation(
  schoolId: string,
  values: Omit<SchoolInvitationCreateRequest, "role">,
) {
  const input = parseApiInput(schoolInvitationCreateRequestSchema, {
    ...values,
    role: "school_admin",
  });
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/invitations`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) {
    await throwApiError(
      response,
      "Nie udało się zaprosić administratora szkoły.",
    );
  }
  return schoolInvitationCreateResponseSchema.parse(await response.json());
}

export async function fetchHqSchoolLimits(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/limits`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać limitów szkoły.");
  return schoolLimitsResponseSchema.parse(await response.json());
}

export async function updateHqSchoolLimits(
  schoolId: string,
  overrides: SchoolLimitOverrides,
) {
  const input = parseApiInput(schoolLimitOverridesSchema, overrides);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/schools/${encodeURIComponent(schoolId)}/limits`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać limitów szkoły.");
  return schoolLimitsResponseSchema.parse(await response.json());
}
